import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Modal, Form } from "react-bootstrap";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import KeywordBox from "./KeywordBox";
import { v4 as uuidv4 } from "uuid";
import jsDig from "utils/jsDig";
import CustomTooltip from "utils/CustomTooltip";

export default function KeywordGroups(props) {
  let keywords = jsDig(["keywordGroups"], props) ? jsDig(["keywordGroups"], props) : [];
  props.onboarding && keywords === null ? (keywords = props.keyword_groups) : undefined;
  if (keywords) {
    if (keywords.length == 0) keywords = [{ frontID: uuidv4(), label: "", conditions: [{ contains: "contains", word: "" }] }];
  }
  const [keywordGroups, setKeywordGroups] = useState(keywords);
  const keywordGroupsOrder = jsDig(["seo_section", "keyword_groups_order"], props) ? jsDig(["seo_section", "keyword_groups_order"], props) : [];
  const defaultSectionOrder = keywordGroupsOrder.length > 0 ? keywordGroupsOrder.map((id) => id.toString()) : [];
  const [sectionOrder, setSectionOrder] = useState(defaultSectionOrder);
  const originalGroupIds = keywords.map((group) => group.id);
  const [showBox, setShowBox] = useState(false);
  const handleDeleteBox = (keywordId) => {
    let removeKeyword;
    const removeState = props.onboarding ? props.keywordGroups : keywordGroups;
    removeKeyword = removeState.filter((item) => {
      return item.id != keywordId;
    });
    if (props.onboarding && props.setKeywordGroups) {
      props.setKeywordGroups([...removeKeyword]);
    }
    setKeywordGroups([...removeKeyword]);
  };
  const handleAddBox = () => {
    let newGroup = {
      id: uuidv4(),
      name: "",
      conditions: [{ contains: "contains", word: "" }],
    };
    if (props.onboarding && props.setKeywordGroups) {
      props.setKeywordGroups([...keywordGroups, newGroup]);
      setSectionOrder([...sectionOrder, newGroup.id]);
    }
    setKeywordGroups([...keywordGroups, newGroup]);
    setSectionOrder([...sectionOrder, newGroup.id]);
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) {
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    let newOrder = [...sectionOrder];
    newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, draggableId);
    setSectionOrder(newOrder);
  };

  return (
    <Container fluid className="top-div seo-config-tab-content p-0 mb-5 pt-3 py-4" style={{ backgroundColor: "#EEEEEE" }}>
      <Row style={{ paddingLeft: "36px", paddingTop: "18px" }} className={props.onboarding ? "justify-content-center" : "justify-content-between mb-4"}>
        {!props.onboarding && (
          <Col lg={9} xs={12} className="pr-5 ">
            <h3 className="green pb-2  visby-bold">Keyword Groups</h3>
            <h5 className="nunito-regular  light-primary">
              In this section, you can configure keyword groups. It's very important that these entries are configured correctly- failing to do so can return
              skewed or undesirable reporting. Click 'Show me some examples' to the right to learn more about properly configuring your keyword groups. Labels
              are for reference only and will not affect the reporting (besides how it is titled in the performance dashboard).
            </h5>
          </Col>
        )}

        <Col className={props.onboarding ? "col-lg-7 pb-2 pt-0 pl-3" : "pb-2 pt-4  mr-4"}>
          <div
            className="row d-flex example-div"
            style={{
              paddingTop: "5px",
              marginRight: "30px",
            }}
            onClick={(e) => setShowBox(true)}>
            <div
              style={{
                cursor: "pointer",
              }}>
              <CustomTooltip faClass="far fa-question-circle pb-1 f16 text-info pt-1 pr-2" inline={true} message={"Click me"} />{" "}
              <span className=" f16 text-info ">Show me some examples</span>
            </div>
          </div>
        </Col>
      </Row>
      <Row  className="justify-content-center">
        <Col lg={props.onboarding ? 7 : 11} xs={12} className="center-from-right p-0">
          <Modal className="tutorial-modal" show={showBox} onHide={(e) => setShowBox(false)}>
            <Modal.Body>
              <Row>
                <Col className="text-right">
                  <p style={{ cursor: "pointer" }} onClick={(e) => setShowBox(false)} className="f22 pr-0">
                    X
                  </p>
                </Col>
              </Row>
              <Row>
                <h2 className="key-modal center-it pb-4">
                  <i className="far fa-question-circle pl-3 mr-3"></i>Setting Up Keyword Groups
                </h2>
              </Row>
              <Row className="px-3">
                <p className="primary">
                  In this section you can filter Google Search Console SEO keyword data into Keyword Groups to be displayed on the client's SEO Dashboard. This
                  will allow you to see trends in certain keyword buckets in SEO. Your objective is to enter <strong>unique keyword phrases</strong> into each
                  field that have no overlap with other fields, to filter the data into a bucket of your choosing. It is important this section is configured
                  correctly, or else you wil have overlapping and inaccurate data. A few importnat notes up-front:
                </p>
              </Row>
              <Row className="p-3">
                <p className="primary">
                  {"1.)"} Keyword filter entries are <strong>not</strong> case sensitive.
                  <br></br>
                  {"2.)"} Be mindful of singular/plural entries and how that could potentially double-count results.
                  <br></br>
                  {"3.)"}Spaces are treated just like any other character. For example, "nikeshoes" and "nike shoes" are unique keyword phrase filters that
                  don't overlap.
                </p>
              </Row>
              <Row className="p-3">
                <p className="primary">
                  Let's imagine we are setting up a client who sells shoes of various brands. You might want a keyword group for "nike shoes" keywords to see
                  how that group of brand-specific keywords is performing. See the scenarios below to see what keyword filters you'll need to add to filter{" "}
                  <strong>only</strong> these keywords properly:
                </p>
              </Row>
              <Row>
                <Col lg className="p-5 ml-0" style={{ backgroundColor: "#E1E1E1" }}>
                  <Row className="mb-3">
                    <h2 className="pr-2">Scenario #1: </h2>
                    <h2 className="green">Good</h2>
                  </Row>
                  <Row className="mb-2">
                    <Form.Control className="" style={{ width: "65%", backgroundColor: "white" }} size="md" disabled={true} value="nike" readOnly />
                  </Row>
                  <Row className="mb-4">
                    <Form.Control className="" style={{ width: "65%", backgroundColor: "white" }} size="md" disabled={true} value="nighkey" readOnly />
                  </Row>
                  <Row className="mb-5"></Row>
                  <Row>
                    <p className="primary">
                      The entries are unique and use the shortest and simplest amount of characters possible to account for all scenarios.
                    </p>
                    <br></br>
                    <p className="primary">-No one keyword filter is contained within any other keyword filter.</p>
                    <br></br>
                    <p className="primary">-We accounted for a misspelling we found in Search Console.</p>
                    <br></br>
                    <p className="primary">-The keyword filters won't pick up other irrelevant keywords.</p>
                    <br></br>
                    <p className="primary">
                      *Note: if the client carried "nike shirts" too, but you only wanted to filter on shoes, this would NOT be okay because "nike" is contained
                      within "nike shirts" and thus would pick up shirts keywords too. See scenario #2.
                    </p>
                  </Row>
                </Col>

                <Col lg className="p-5 mx-4 trim-margin" style={{ backgroundColor: "#E1E1E1" }}>
                  <Row className="mb-3">
                    <h2 className="pr-2">Scenario #2: </h2>
                    <h2 className="green">Good</h2>
                  </Row>
                  <Row className="mb-2">
                    <Form.Control className="" style={{ width: "65%", backgroundColor: "white" }} size="md" disabled={true} value="nike shoes" readOnly />
                  </Row>
                  <Row className="mb-2">
                    <Form.Control className="" style={{ width: "65%", backgroundColor: "white" }} size="md" disabled={true} value="nighkey shoes" readOnly />
                  </Row>
                  <Row className="mb-4">
                    <Form.Control className="" style={{ width: "65%", backgroundColor: "white" }} size="md" disabled={true} value="nikeshoes" readOnly />
                  </Row>
                  <Row>
                    <p className="primary">
                      If the client carried "Nike shirts" too, we would change the inputs to this scenario to only capture the shoes keywords.
                    </p>
                    <br></br>
                    <p className="primary">
                      This also <strong>doesn't</strong> include overlap - even though the word "shoes" is in all keyword filters above, these are treated as{" "}
                      <strong>phrases</strong>, so no entire keyword phrase is contained within any other phrase.
                    </p>
                  </Row>
                </Col>
                <Col lg className="p-5 mr-0" style={{ backgroundColor: "#E1E1E1" }}>
                  <Row className="mb-3">
                    <h2 className="pr-2">Scenario #3: </h2>
                    <h2 className="red">Not Good</h2>
                  </Row>
                  <Row>
                    <Form.Control className="mb-2 red" style={{ width: "65%", backgroundColor: "white" }} size="md" disabled={true} value="nike" readOnly />
                  </Row>
                  <Row className="mb-2">
                    <Form.Control className="red" style={{ width: "65%", backgroundColor: "white" }} size="md" disabled={true} value="nike shoe" readOnly />
                  </Row>
                  <Row className="mb-4">
                    <Form.Control className="red" style={{ width: "65%", backgroundColor: "white" }} size="md" disabled={true} value="shoes" readOnly />
                  </Row>
                  <Row>
                    <p className="primary">This list of keyword filters would have overlap, causing double {`(maybe triple) `}counting of the data.</p>
                    <br></br>
                    <p className="primary">
                      The first entry "nike" is contained within the second entry "nike shoe". Also, the third entry "shoes" would pick up irrelevant keywords
                      like "adidas shoes" in Google Search Console.
                    </p>
                  </Row>
                </Col>
              </Row>
            </Modal.Body>
          </Modal>

          <Button
            className="btn-lg key-button mb-3"
            
            as="input"
            type="button"
            value="+ Add Keyword Group"
            variant="success"
            onClick={handleAddBox}
          />
        </Col>
      </Row>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="keywords-groups-droppable" direction="horizontal">
          {(provided) => (
            <div className="keyword-groups pl-4 pl-xs-0 justify-content-lg-start justify-content-md-center" {...provided.droppableProps} ref={provided.innerRef}>
              {sectionOrder.map((kgGroupId, idx) => {
                const keyword = props.onboarding
                  ? props.keywordGroups.find((group) => group.id == kgGroupId)
                  : keywordGroups.find((group) => group.id == kgGroupId);
                const index = props.onboarding
                  ? props.keywordGroups.findIndex((group) => group.id == kgGroupId)
                  : keywordGroups.findIndex((group) => group.id == kgGroupId);
                if (keyword) {
                  return (
                    <div className="keyword-item mr-2 mb-1" style={{ backgroundColor: "#EEEEEE" }} key={keyword.id}>
                      {props.onboarding ? (
                        <KeywordBox
                          name={keyword.name}
                          index={index}
                          label={keyword.label}
                          conditions={keyword.conditions}
                          handleDelete={handleDeleteBox}
                          id={kgGroupId}
                          onboarding={props.onboarding}
                          keywordGroups={props.keywordGroups}
                          setKeywordGroups={props.setKeywordGroups}
                          sectionOrder={sectionOrder}
                          setSectionOrder={setSectionOrder}
                        />
                      ) : (
                        <KeywordBox
                          name={keyword.name}
                          index={idx}
                          label={keyword.label}
                          conditions={keyword.conditions}
                          handleDelete={handleDeleteBox}
                          id={kgGroupId}
                          keywordGroups={keywordGroups}
                          setKeywordGroups={setKeywordGroups}
                          sectionOrder={sectionOrder}
                          setSectionOrder={setSectionOrder}
                        />
                      )}
                    </div>
                  );
                }
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Container>
  );
}
